import React, {useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Alert, Card, CardBody, Spinner} from 'react-bootstrap'
import {useParams} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {isAxiosNotFound, parseApiMessage, verifyDocument} from '../../../components/admin-documents/adminDocumentsApi'
import {DOCUMENT_TYPE_LABELS, pickDocLabel, type DocumentLabelLocale} from '../../../components/admin-documents/documentLabels'
import type {DocumentVerifyResult} from '../../../components/admin-documents/types'

export default function DocumentVerifyPage() {
  const {code = ''} = useParams<{code: string}>()
  const intl = useIntl()
  const locale: DocumentLabelLocale = intl.locale.startsWith('ar')
    ? 'ar'
    : intl.locale.startsWith('en')
      ? 'en'
      : 'fr'
  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState<DocumentVerifyResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!code) {
      setLoading(false)
      setError('Missing verification code')
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await verifyDocument(code)
        if (!cancelled) setResult(data)
      } catch (e) {
        if (!cancelled) {
          if (isAxiosNotFound(e)) {
            setResult({success: false, valid: false})
          } else {
            setError(parseApiMessage(e, locale))
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [code, locale])

  const typeLabel = (type?: string) => {
    if (!type) return '—'
    const entry = DOCUMENT_TYPE_LABELS[type as keyof typeof DOCUMENT_TYPE_LABELS]
    return entry ? pickDocLabel(entry, locale) : type
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        <FormattedMessage id='ADMIN_DOCUMENTS.VERIFY_TITLE' defaultMessage='Document verification' />
      </PageTitle>
      <Card className='mx-auto' style={{maxWidth: 520}}>
        <CardBody className='text-center py-10'>
          {loading ? (
            <Spinner animation='border' />
          ) : error ? (
            <Alert variant='danger'>{error}</Alert>
          ) : result?.valid ? (
            <>
              <div className='fs-1 mb-4 text-success'>✓</div>
              <h2 className='fs-3 fw-bold text-success mb-4'>
                <FormattedMessage id='ADMIN_DOCUMENTS.VERIFY_VALID' defaultMessage='Valid document' />
              </h2>
              <p className='mb-1'>
                <strong>{result.documentNumber}</strong>
              </p>
              <p className='text-muted mb-1'>{typeLabel(result.type)}</p>
              <p className='text-muted mb-1'>{result.schoolYear}</p>
              {result.student ? (
                <p className='mb-1'>
                  {result.student.firstName} {result.student.lastName}
                </p>
              ) : null}
              {result.establishment?.name ? (
                <p className='text-muted small'>{result.establishment.name}</p>
              ) : null}
            </>
          ) : (
            <>
              <div className='fs-1 mb-4 text-danger'>✗</div>
              <h2 className='fs-3 fw-bold text-danger'>
                <FormattedMessage id='ADMIN_DOCUMENTS.VERIFY_INVALID' defaultMessage='Invalid or unknown document' />
              </h2>
              {result?.message ? (
                <p className='text-muted'>{result.message.fr || result.message.en}</p>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}
